import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  PieChart,
  ShoppingBag,
  ShoppingCart,
  Coffee,
  Bus,
  Play,
  Zap,
  Dumbbell,
  Plane,
  Landmark,
  Sparkles,
} from "lucide-react";
import { CATEGORIES, detectMerchant } from "@/lib/merchants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = {
  ShoppingBag,
  ShoppingCart,
  Coffee,
  Bus,
  Play,
  Zap,
  Dumbbell,
  Plane,
  Landmark,
};

const OTHER = {
  key: "other",
  color: "#94A3B8",
  label: "Other",
  icon: "Landmark",
};

const LOCALES = { GBP: "en-GB", EUR: "en-IE", USD: "en-US", CAD: "en-CA", AUD: "en-AU" };

const formatAmount = (amount, currency) => {
  try {
    return new Intl.NumberFormat(LOCALES[currency] || "en-GB", {
      style: "currency",
      currency: currency || "GBP",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount)} ${currency || ""}`.trim();
  }
};

// Convert `amount` from `fromCurrency` into `homeCurrency` using Frankfurter-shaped
// fxRates payload: `{ base: "GBP", rates: { EUR: 1.17, USD: 1.27 } }`.
// If we can't resolve a rate, fall back to the raw amount (best-effort — the widget
// makes it clear amounts are in home currency, but showing a rough sum is better than
// hiding real spend just because one currency was missing).
const convertToHome = (amount, fromCurrency, homeCurrency, fxRates) => {
  if (!amount || !fromCurrency) return 0;
  const from = fromCurrency.toUpperCase();
  const home = (homeCurrency || "GBP").toUpperCase();
  if (from === home) return amount;
  if (!fxRates || !fxRates.rates) return amount;
  const base = (fxRates.base || home).toUpperCase();
  const rates = fxRates.rates;
  // Frankfurter returns rates with `base` as the anchor. amount_in_home = amount * (rate_home / rate_from)
  const rateHome = base === home ? 1 : rates[home];
  const rateFrom = base === from ? 1 : rates[from];
  if (!rateHome || !rateFrom) return amount;
  return (amount * rateHome) / rateFrom;
};

const startOfMonthIso = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
};

const CategoryBreakdown = ({ userId, refreshKey = 0, homeCurrency = "GBP", fxRates = null }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchItems = async () => {
      setLoading(true);
      try {
        const params = { limit: 200 };
        if (userId) params.user_id = userId;
        const res = await axios.get(`${API}/cards/transactions`, {
          params,
          withCredentials: true,
        });
        if (!cancelled) setItems(res.data?.data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchItems();
    return () => {
      cancelled = true;
    };
  }, [userId, refreshKey]);

  const { totals, grandTotal, sinceLabel } = useMemo(() => {
    const monthStart = startOfMonthIso();
    const buckets = new Map(); // key -> { key, label, color, icon, amount }
    let grand = 0;
    for (const it of items) {
      if (it.type !== "expense") continue; // only spend
      let cat = null;
      if (it.source === "plaid") {
        const m = detectMerchant(it.description);
        cat = m ? { key: m.category, ...CATEGORIES[m.category] } : OTHER;
      } else if (it.category && CATEGORIES[it.category]) {
        // Manual expense with a user-picked category (added via ManualCardDialog)
        cat = { key: it.category, ...CATEGORIES[it.category] };
      } else if (it.category === "other") {
        cat = OTHER;
      } else {
        continue; // manual balance edits without a category are opaque
      }
      if (!it.at || it.at < monthStart) continue; // month-to-date only
      const home = convertToHome(Number(it.amount) || 0, it.currency, homeCurrency, fxRates);
      grand += home;
      const b = buckets.get(cat.key) || { ...cat, amount: 0 };
      b.amount += home;
      buckets.set(cat.key, b);
    }
    const sorted = Array.from(buckets.values()).sort((a, b) => b.amount - a.amount);
    return {
      totals: sorted,
      grandTotal: grand,
      sinceLabel: new Date().toLocaleDateString(undefined, { month: "long" }),
    };
  }, [items, homeCurrency, fxRates]);

  const top = totals.slice(0, 5);
  const restTotal = totals.slice(5).reduce((n, b) => n + b.amount, 0);
  const hasData = grandTotal > 0 && top.length > 0;

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.18 }}
      className="mt-8 rounded-2xl border border-white/5 bg-white/[0.02] px-5 sm:px-6 py-5"
      data-testid="category-breakdown-panel"
    >
      <header className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center flex-shrink-0">
            <PieChart size={14} className="text-[#00E676]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#00E676] font-bold">
              Where your money goes
            </div>
            <h3
              className="text-base sm:text-lg text-white font-semibold truncate"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="category-breakdown-title"
            >
              {sinceLabel} spending by category
            </h3>
          </div>
        </div>
        {hasData && (
          <div className="text-right flex-shrink-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
              Total this month
            </div>
            <div
              className="text-lg sm:text-xl font-semibold text-white mt-0.5"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="category-breakdown-total"
            >
              {formatAmount(grandTotal, homeCurrency)}
            </div>
          </div>
        )}
      </header>

      {!hasData ? (
        <div
          className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] py-5 px-4 text-center"
          data-testid="category-breakdown-empty"
        >
          <div className="mx-auto w-8 h-8 rounded-full bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center">
            <Sparkles size={13} className="text-[#00E676]" />
          </div>
          <p className="mt-3 text-sm text-slate-300 max-w-md mx-auto">
            {loading
              ? "Loading this month's spending…"
              : "No categorised spending this month yet. Log an expense on any card (or connect a bank) and we'll break it down by Groceries, Food, Transport and more."}
          </p>
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="category-breakdown-list">
          {top.map((b) => {
            const Icon = ICON_MAP[b.icon] || Landmark;
            const pct = grandTotal > 0 ? (b.amount / grandTotal) * 100 : 0;
            return (
              <li
                key={b.key}
                className="rounded-xl border border-white/5 bg-white/[0.02] px-3 sm:px-4 py-2.5"
                data-testid={`category-breakdown-item-${b.key}`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${b.color}18`,
                      border: `1px solid ${b.color}55`,
                      color: b.color,
                    }}
                  >
                    <Icon size={13} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-white font-semibold truncate">
                          {b.label}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {pct.toFixed(0)}% of total
                        </div>
                      </div>
                      <div
                        className="text-sm font-semibold text-white whitespace-nowrap"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                        data-testid={`category-breakdown-amount-${b.key}`}
                      >
                        {formatAmount(b.amount, homeCurrency)}
                      </div>
                    </div>
                    <div
                      className="mt-2 h-1.5 w-full rounded-full bg-white/5 overflow-hidden"
                      aria-hidden="true"
                    >
                      <div
                        className="h-full rounded-full transition-[width] duration-500"
                        style={{
                          width: `${Math.max(4, Math.min(100, pct))}%`,
                          background: b.color,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
          {restTotal > 0 && (
            <li
              className="flex items-center justify-between px-3 sm:px-4 py-1.5 text-[11px] text-slate-400"
              data-testid="category-breakdown-rest"
            >
              <span>+ {totals.length - top.length} more categories</span>
              <span className="text-slate-300">{formatAmount(restTotal, homeCurrency)}</span>
            </li>
          )}
        </ul>
      )}
    </motion.section>
  );
};

export default CategoryBreakdown;
